// packages/decision-ui/src/components/BaselineVsEnhanced/BaselineVsEnhanced.tsx

import React, { useState } from 'react';

export interface BaselineOption {
  title: string;
  subtitle: string;
  label: string;
  boilerName: string;
  confidenceScore: number;
  risks: string[];
  warranties: string[];
  styling: 'baseline' | 'enhanced';
}

export interface EnhancedOption {
  title: string;
  subtitle: string;
  label: string;
  boilerName: string;
  // Enablers (no costs - just list of what's included)
  includesFilter: boolean;
  includesFlush: boolean;
  includesControls: boolean;
  includesZoning: boolean;
  confidenceScore: number;
  benefits: string[];
  warranties: string[];
  styling: 'baseline' | 'enhanced';
}

export interface BaselineVsEnhancedProps {
  baselineOption: BaselineOption;
  enhancedOption: EnhancedOption;
  onSelectOption?: (option: 'baseline' | 'enhanced') => void;
  selectedOption?: 'baseline' | 'enhanced';
}

const BaselineVsEnhanced: React.FC<BaselineVsEnhancedProps> = ({
  baselineOption,
  enhancedOption,
  onSelectOption,
  selectedOption,
}) => {
  const [hoveredCard, setHoveredCard] = useState<'baseline' | 'enhanced' | null>(null);

  const handleCardClick = (option: 'baseline' | 'enhanced') => {
    onSelectOption?.(option);
  };

  const renderConfidenceBar = (score: number, styling: 'baseline' | 'enhanced') => {
    const color = styling === 'baseline' ? '#6c757d' : '#28a745';
    const bgColor = styling === 'baseline' ? '#e9ecef' : '#d4edda';
    
    return (
      <div style={{ marginTop: '1rem' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginBottom: '0.5rem',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Confidence Score</span>
          <span style={{ fontWeight: 700, fontSize: '0.875rem', color }}>{score}%</span>
        </div>
        <div style={{
          width: '100%',
          height: '12px',
          backgroundColor: bgColor,
          borderRadius: '6px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${score}%`,
            height: '100%',
            backgroundColor: color,
            transition: 'width 0.5s ease',
          }} />
        </div>
        {styling === 'baseline' && score < 80 && (
          <div style={{ 
            fontSize: '0.75rem', 
            color: '#856404',
            marginTop: '0.5rem',
            fontStyle: 'italic',
          }}>
            ⚠️ Lower confidence due to existing system risks
          </div>
        )}
      </div>
    );
  };

  const renderBaselineCard = () => {
    const isSelected = selectedOption === 'baseline';
    const isHovered = hoveredCard === 'baseline';
    
    return (
      <div
        onClick={() => handleCardClick('baseline')}
        onMouseEnter={() => setHoveredCard('baseline')}
        onMouseLeave={() => setHoveredCard(null)}
        style={{
          flex: 1,
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          border: isSelected ? '3px solid #6c757d' : '2px solid #dee2e6',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
          boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
          filter: 'grayscale(30%)',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'inline-block',
            padding: '0.5rem 1rem',
            backgroundColor: '#6c757d',
            color: '#fff',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '1rem',
          }}>
            {baselineOption.label}
          </div>
          <h3 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700,
            margin: '0 0 0.5rem 0',
            color: '#212529',
          }}>
            {baselineOption.title}
          </h3>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#6c757d',
            margin: 0,
          }}>
            {baselineOption.subtitle}
          </p>
        </div>

        {/* Boiler Details */}
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff',
          borderRadius: '8px',
          marginBottom: '1rem',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
            {baselineOption.boilerName}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
            <div>• Boiler unit</div>
            <div>• Standard flue</div>
            <div>• Basic connection</div>
          </div>
        </div>

        {/* Scope Summary */}
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '2px solid #6c757d',
        }}>
          <div style={{ 
            fontSize: '0.875rem', 
            color: '#6c757d',
            marginBottom: '0.25rem',
          }}>
            Scope model
          </div>
          <div style={{ 
            fontSize: '1.25rem', 
            fontWeight: 700,
            color: '#212529',
          }}>
            Half measures (minimum)
          </div>
        </div>

        {/* Confidence Score */}
        {renderConfidenceBar(baselineOption.confidenceScore, 'baseline')}

        {/* Omission penalties / risks */}
        <div style={{ marginTop: '1.5rem' }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: 600,
            marginBottom: '0.75rem',
            color: '#dc3545',
          }}>
            ⚠️ Penalties if work is omitted
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '1.25rem',
            fontSize: '0.875rem',
            color: '#6c757d',
          }}>
            {baselineOption.risks.map((risk, idx) => (
              <li key={idx} style={{ marginBottom: '0.5rem' }}>{risk}</li>
            ))}
          </ul>
        </div>

        {/* Warranties */}
        <div style={{ marginTop: '1.5rem' }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: 600,
            marginBottom: '0.75rem',
          }}>
            Warranties
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '1.25rem',
            fontSize: '0.875rem',
            color: '#6c757d',
          }}>
            {baselineOption.warranties.map((warranty, idx) => (
              <li key={idx} style={{ marginBottom: '0.5rem' }}>{warranty}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderEnhancedCard = () => {
    const isSelected = selectedOption === 'enhanced';
    const isHovered = hoveredCard === 'enhanced';
    
    return (
      <div
        onClick={() => handleCardClick('enhanced')}
        onMouseEnter={() => setHoveredCard('enhanced')}
        onMouseLeave={() => setHoveredCard(null)}
        style={{
          flex: 1,
          padding: '2rem',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          borderRadius: '12px',
          border: isSelected ? '3px solid #ffd700' : '2px solid #764ba2',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          boxShadow: isHovered ? '0 12px 32px rgba(102, 126, 234, 0.4)' : '0 8px 24px rgba(102, 126, 234, 0.2)',
          position: 'relative',
        }}
      >
        {/* "Hero" Badge */}
        <div style={{
          position: 'absolute',
          top: '-12px',
          right: '24px',
          padding: '0.5rem 1rem',
          backgroundColor: '#ffd700',
          color: '#212529',
          borderRadius: '20px',
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
        }}>
          🌟 Full upgrade (recommended)
        </div>

        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'inline-block',
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
            color: '#fff',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '1rem',
          }}>
            {enhancedOption.label}
          </div>
          <h3 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 700,
            margin: '0 0 0.5rem 0',
            color: '#fff',
          }}>
            {enhancedOption.title}
          </h3>
          <p style={{ 
            fontSize: '1rem', 
            color: 'rgba(255, 255, 255, 0.9)',
            margin: 0,
          }}>
            {enhancedOption.subtitle}
          </p>
        </div>

        {/* The Solution */}
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '8px',
          marginBottom: '1rem',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            💎 The Solution
          </div>
          <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem' }}>
            {enhancedOption.boilerName}
          </div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
            <div>• Boiler unit</div>
            <div>• Standard flue</div>
            <div>• Basic connection</div>
          </div>
        </div>

        {/* The Enablers */}
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '8px',
          marginBottom: '1rem',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            ✨ The Enablers
          </div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
            {enhancedOption.includesFilter && <div>• Laminar Flow Filter</div>}
            {enhancedOption.includesFlush && <div>• Full Chemical Flush</div>}
            {enhancedOption.includesControls && <div>• Weather Compensation</div>}
            {enhancedOption.includesZoning && <div>• Advanced Zoning</div>}
          </div>
        </div>

        {/* Scope Summary */}
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.25)',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ 
            fontSize: '0.875rem', 
            opacity: 0.9,
            marginBottom: '0.25rem',
          }}>
            Scope model
          </div>
          <div style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700,
            color: '#fff',
          }}>
            Full upgrade (recommended)
          </div>
          <div style={{ 
            fontSize: '0.75rem', 
            opacity: 0.8,
            marginTop: '0.25rem',
          }}>
            Boiler + Protection + Optimization
          </div>
        </div>

        {/* Confidence Score */}
        <div style={{ marginTop: '1rem' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
          }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Confidence Score</span>
            <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{enhancedOption.confidenceScore}%</span>
          </div>
          <div style={{
            width: '100%',
            height: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '6px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${enhancedOption.confidenceScore}%`,
              height: '100%',
              backgroundColor: '#ffd700',
              transition: 'width 0.5s ease',
            }} />
          </div>
          {enhancedOption.confidenceScore >= 95 && (
            <div style={{ 
              fontSize: '0.875rem', 
              marginTop: '0.5rem',
              fontWeight: 600,
            }}>
              ✅ "Bulletproof" System Protection
            </div>
          )}
        </div>

        {/* Benefits */}
        <div style={{ marginTop: '1.5rem' }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: 600,
            marginBottom: '0.75rem',
          }}>
            🎯 Whole-House Benefits
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '1.25rem',
            fontSize: '0.875rem',
            opacity: 0.95,
          }}>
            {enhancedOption.benefits.map((benefit, idx) => (
              <li key={idx} style={{ marginBottom: '0.5rem' }}>{benefit}</li>
            ))}
          </ul>
        </div>

        {/* Warranties */}
        <div style={{ marginTop: '1.5rem' }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: 600,
            marginBottom: '0.75rem',
          }}>
            Extended Warranties
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '1.25rem',
            fontSize: '0.875rem',
            opacity: 0.95,
          }}>
            {enhancedOption.warranties.map((warranty, idx) => (
              <li key={idx} style={{ marginBottom: '0.5rem' }}>{warranty}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ 
          fontSize: '2rem', 
          fontWeight: 700,
          margin: '0 0 0.5rem 0',
        }}>
          Baseline vs Blueprint
        </h2>
        <p style={{ 
          fontSize: '1rem', 
          color: '#6c757d',
          margin: 0,
        }}>
          Choose between essential compliance or a whole-system transformation
        </p>
      </div>

      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '2rem',
        maxWidth: '1400px',
        margin: '0 auto',
      }}>
        {renderBaselineCard()}
        {renderEnhancedCard()}
      </div>

      {selectedOption && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          backgroundColor: '#e7f3ff',
          borderRadius: '8px',
          border: '1px solid #b3d9ff',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: '#0056b3' }}>
            You selected: <strong>{selectedOption === 'baseline' ? 'Half measures (minimum)' : 'Full upgrade (recommended)'}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaselineVsEnhanced;
